import { Button } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { RegistryAllocation } from '../../api/registryEntityTypes';
import { allocationKey } from '../../entities/entitySource';
import { applicationForAllocation } from '../../store/handlers/allocationHandlers';
import { useRegistry } from '../../store/RegistryStore';
import DataTable from '../../ui/DataTable';
import { statusLabel } from '../../ui/statusTones';
import { AMOUNT_COLUMN, contextColumn, INVESTOR_COLUMN } from './allocationColumns';
import StartApplicationDialog from './StartApplicationDialog';

/**
 * Investor allocations (read only; made in the admin portal). Each can start a sample application.
 * `groupBy` picks the context column: the fund opportunity on an entity, the entity on a fund.
 */
export default function AllocationsTable({
  allocations,
  groupBy,
  ownerEmail,
}: {
  allocations: RegistryAllocation[];
  groupBy: 'entity' | 'fundOpportunity';
  ownerEmail?: (allocation: RegistryAllocation) => string | undefined;
}) {
  const { state } = useRegistry();
  const [starting, setStarting] = useState<RegistryAllocation | null>(null);

  const applicationCell = (row: RegistryAllocation) => {
    const application = applicationForAllocation(state.applications, allocationKey(row));
    if (application)
      return (
        <RouterLink to={`/manager/applications/${application.id}`}>
          {application.reference} · {statusLabel(application.status)}
        </RouterLink>
      );
    return (
      <Button size="small" variant="outlined" onClick={() => setStarting(row)}>
        Start application
      </Button>
    );
  };

  return (
    <>
      <DataTable
        rows={allocations}
        rowKey={allocationKey}
        dense
        emptyMessage="No allocations yet. Allocations are made in the admin portal."
        columns={[
          contextColumn(groupBy),
          INVESTOR_COLUMN,
          AMOUNT_COLUMN,
          { key: 'application', label: 'Application (sample)', render: applicationCell },
        ]}
      />
      <StartApplicationDialog
        allocation={starting}
        email={starting ? ownerEmail?.(starting) : undefined}
        onClose={() => setStarting(null)}
      />
    </>
  );
}
